import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import OptimizedImage from './components/OptimizedImage/OptimizedImage';
import './App.css';
import './LandingPage.css';
import {
  PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL,
  PREMIUM_PAYMENT_TRUST_LINE,
  PREMIUM_PRICE_PERIOD_LABEL,
  PREMIUM_PRICE_SHORT_LABEL,
  PREMIUM_UPGRADE_CTA_LABEL,
} from './constants/pricing';
import {
  PROMO_CHAT_HERO_MOTION,
  PROMO_SCREENSHOTS,
} from './constants/promoImages';
import { usePremiumUpgrade } from './hooks/usePremiumUpgrade';
import { basisPlanFeatures, premiumPlanFeatures } from './constants/pricingFeatures';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import CheckoutErrorNotice from './components/CheckoutErrorNotice/CheckoutErrorNotice';
import { useReducedMotion } from './motion/useReducedMotion';

function useScrollReveal() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current || document;
    const elements = container.querySelectorAll('.reveal');

    const isMobile = window.innerWidth <= 768;
    const threshold = isMobile ? 0.05 : 0.2;
    const rootMargin = isMobile ? '50px' : '0px';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          } else if (!isMobile) {
            entry.target.classList.remove('visible');
          }
        });
      },
      { threshold, rootMargin }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return containerRef;
}

const containerVariants = {
  animate: {
    transition: { staggerChildren: 0.12, delayChildren: 0 },
  },
};

const fadeUp = {
  initial: { y: 20, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] },
  },
};

const latestReportsVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const reportCardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] },
  },
};

const LANDING_FEATURES = [
  {
    id: 'map',
    number: '01 — Kortvisning',
    title: 'Se alle institutioner på ét interaktivt kort',
    description:
      'Med kortvisningen (Google Maps) kan du visuelt udforske institutioner i dit nærområde. Filtrer på bydel, kapacitet og karakterer — og find de bedste muligheder tæt på dig eller din arbejdsplads.',
    pills: ['Google Maps integration', 'Filtrer på bydel', 'Sammenlign institutioner'],
    image: PROMO_SCREENSHOTS.map,
    imageAlt: 'Kortvisning af institutioner med Google Maps',
    reverse: false,
    premiumLock: true,
  },
  {
    id: 'trend',
    number: '02 — Historiske trends',
    title: 'Følg en institutions udvikling over tid',
    description:
      'Historiske trends viser, hvordan en institution har klaret tilsynene igennem flere år. Er kvaliteten stigende? Har der været vedvarende problemer? Med trendgrafen kan du se det med det samme — og undgå ubehagelige overraskelser.',
    pills: ['Flerårige tilsynsdata', 'Trendgraf', 'Sammenlign institutioner'],
    image: PROMO_SCREENSHOTS.trend,
    imageAlt: 'Historiske trends for en institution',
    reverse: true,
    premiumLock: true,
  },
  {
    id: 'chat',
    number: '03 — AI-Assistent',
    title: 'Stil spørgsmål direkte om institutionen',
    description:
      'Med Premium kan du spørge tilsynsrapporten på dansk, når du skal vælge. Svarene bygger på den officielle rapport og viser, hvor i teksten de kommer fra.',
    pills: ['Dansk AI-svar', 'Baseret på tilsynsrapporter', 'Kun med Premium'],
    image: PROMO_SCREENSHOTS.chat,
    imageAlt: 'AI-assistent besvarer spørgsmål om institution i et chat-vindue',
    reverse: false,
    premiumLock: true,
  },
];

const BASIS_PLAN_FEATURES = basisPlanFeatures();
const PREMIUM_PLAN_FEATURES = premiumPlanFeatures();

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function StarIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function BrowserChrome({ children }) {
  return (
    <div className="browser-chrome">
      <div className="browser-bar">
        <div className="browser-dots">
          <div className="browser-dot" />
          <div className="browser-dot" />
          <div className="browser-dot" />
        </div>
        <div className="browser-url">vuggestueguiden.dk</div>
      </div>
      <div className="browser-body">{children}</div>
    </div>
  );
}

function getHeroScreenshotTransition() {
  return {
    duration: PROMO_CHAT_HERO_MOTION.durationS,
    times: [0, 0.18, 0.48, 0.65, 0.95, 1],
    ease: ['linear', 'easeInOut', 'linear', 'easeInOut', 'linear'],
    repeat: Infinity,
  };
}

function HeroScreenshot({ reducedMotion }) {
  const bottomOffset = `${PROMO_CHAT_HERO_MOTION.bottomOffsetPercent}%`;

  return (
    <motion.div
      className="landing-hero__screenshot-zoom"
      animate={
        reducedMotion
          ? { y: 0 }
          : {
              y: ['0%', '0%', bottomOffset, bottomOffset, '0%', '0%'],
            }
      }
      transition={reducedMotion ? undefined : getHeroScreenshotTransition()}
    >
      <img
        src={PROMO_SCREENSHOTS.chat}
        alt="AI-assistent besvarer spørgsmål om en institution"
        width={800}
        height={600}
        loading="eager"
        className="landing-hero__screenshot"
      />
    </motion.div>
  );
}

function scrollToFeatures() {
  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
}

export default function LandingPage() {
  const navigate = useNavigate();
  const containerRef = useScrollReveal();
  const reducedMotion = useReducedMotion();
  const [latestReports, setLatestReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const latestReportsRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const isInView = useInView(latestReportsRef, {
    once: true,
    margin: isMobile ? '0px' : '-100px',
  });

  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath: '/',
    source: 'landing',
    placement: 'landing',
    feature: 'general',
  });

  const baseURL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    const fetchLatestReports = async () => {
      try {
        setReportsLoading(true);
        const response = await axios.get(`${baseURL}/latest-reports/`);
        setLatestReports(response.data.results || []);
      } catch (error) {
        console.error('Error fetching latest reports:', error);
        setLatestReports([]);
      } finally {
        setReportsLoading(false);
      }
    };

    fetchLatestReports();
  }, [baseURL]);

  return (
    <motion.div ref={containerRef} className="app-scroll-container landing-page-dark" initial="initial" animate="animate">
      {/* Hero */}
      <section className="landing-hero-section">
        <div className="landing-container">
          <motion.div
            variants={containerVariants}
            initial="initial"
            animate="animate"
            className="landing-hero__inner reveal"
          >
            <div className="landing-hero__copy">
              <motion.div variants={fadeUp} className="landing-hero__eyebrow">
                <StarIcon size={12} />
                Bedre data. Gladere børn.
              </motion.div>
              <motion.h1 variants={fadeUp} className="landing-hero__title">
                Find et sted,
                <br />
                hvor dit barn <em>trives.</em>
              </motion.h1>
              <motion.p variants={fadeUp} className="landing-hero__subtitle">
                Sammenlign daginstitutioner på det, der faktisk betyder noget. Vuggestueguiden samler officielle
                tilsynsrapporter, udvikling og praktisk information ét sted.
              </motion.p>
            </div>

            <motion.div variants={fadeUp} className="landing-hero__visual">
              <BrowserChrome>
                <HeroScreenshot reducedMotion={reducedMotion} />
              </BrowserChrome>
            </motion.div>

            <div className="landing-hero__actions">
              <motion.div variants={fadeUp} className="landing-hero__cta">
                <button type="button" className="landing-btn-hero" onClick={() => navigate('/institution-list')}>
                  Find din institution
                  <ArrowIcon />
                </button>
                <button type="button" className="landing-btn-hero-ghost" onClick={scrollToFeatures}>
                  Se hvordan det virker
                </button>
              </motion.div>
              <motion.div variants={fadeUp} className="landing-price-badge">
                <span className="landing-price-badge__star" aria-hidden="true">
                  ✦
                </span>
                <span className="landing-price-badge__amount">Grundlaget for et trygt valg</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features" id="features">
        <div className="landing-container">
          <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-80px' }}
            className="landing-features__header reveal"
          >
            <motion.p variants={fadeUp} className="landing-section-eyebrow">
              <StarIcon />
              Premium-funktioner
            </motion.p>
            <motion.h2 variants={fadeUp} className="landing-section-title">
              Alt du behøver for at træffe
              <br />
              det <em>rigtige valg</em>
            </motion.h2>
            <motion.p variants={fadeUp} className="landing-section-lead">
              Vuggestueguiden Premium giver dig de data og værktøjer, der gør forskel — så du kan vælge trygt og
              informeret.
            </motion.p>
          </motion.div>

          {LANDING_FEATURES.map((feature) => (
            <motion.div
              key={feature.id}
              variants={containerVariants}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-60px' }}
              className={`landing-feature-block reveal${feature.reverse ? ' landing-feature-block--reverse' : ''}`}
            >
              <div className="landing-feature-block__text">
                <motion.p variants={fadeUp} className="landing-feature-block__number">
                  {feature.number}
                </motion.p>
                <motion.h3 variants={fadeUp} className="landing-feature-block__title">
                  {feature.title}
                </motion.h3>
                <motion.p variants={fadeUp} className="landing-feature-block__desc">
                  {feature.description}
                </motion.p>
                <motion.div variants={fadeUp} className="landing-feature-pills">
                  {feature.pills.map((pill) => (
                    <span key={pill} className="landing-pill">
                      {pill}
                    </span>
                  ))}
                </motion.div>
              </div>
              <motion.div variants={fadeUp} className="landing-feature-block__visual">
                <div className="landing-screenshot-glow" aria-hidden="true" />
                <div className="landing-screenshot-frame">
                  <OptimizedImage
                    src={feature.image}
                    webpSrc={false}
                    alt={feature.imageAlt}
                    width={800}
                    height={500}
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  {feature.premiumLock && (
                    <div className="landing-lock-badge">
                      <LockIcon />
                      Premium
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="landing-pricing" id="priser">
        <div className="landing-container landing-container--narrow">
          <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="landing-pricing__header reveal"
          >
            <motion.p variants={fadeUp} className="landing-section-eyebrow">
              Prissammenligning
            </motion.p>
            <motion.h2 variants={fadeUp} className="landing-section-title">
              Basis vs. <em>Premium</em>
            </motion.h2>
            <motion.p variants={fadeUp} className="landing-section-lead">
              119 DKK som engangsbetaling giver 1 års adgang til observationer, historik og svaret på,
              hvilken institution der står stærkest. Adgangen fornyes ikke.
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="landing-pricing-cards reveal"
          >
            <motion.div variants={fadeUp} className="landing-pricing-card">
              <div className="landing-plan-badge landing-plan-badge--spacer" aria-hidden="true">
                Anbefalet
              </div>
              <p className="landing-plan-name">Basis</p>
              <div className="landing-plan-price">
                <span className="landing-plan-price__amount">Gratis</span>
              </div>
              <ul className="landing-plan-features">
                {BASIS_PLAN_FEATURES.map((item) => (
                  <li key={item.text} className={item.included ? 'included' : ''}>
                    <span className={`landing-feature-check landing-feature-check--${item.included ? 'yes' : 'no'}`}>
                      {item.included ? <CheckIcon /> : <XIcon />}
                    </span>
                    {item.text}
                  </li>
                ))}
              </ul>
              <button type="button" className="landing-plan-cta landing-plan-cta--ghost" onClick={() => navigate('/register')}>
                Fortsæt gratis
              </button>
            </motion.div>

            <motion.div variants={fadeUp} className="landing-pricing-card landing-pricing-card--premium">
              <div className="landing-plan-badge">⭐ Anbefalet</div>
              <p className="landing-plan-name">Premium</p>
              <div className="landing-plan-price">
                <span className="landing-plan-price__amount">{PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL}</span>
                <span className="landing-plan-price__period">{PREMIUM_PRICE_PERIOD_LABEL}</span>
              </div>
              <ul className="landing-plan-features">
                {PREMIUM_PLAN_FEATURES.map((text) => (
                  <li key={text} className="included">
                    <span className="landing-feature-check landing-feature-check--yes">
                      <CheckIcon />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="landing-plan-cta landing-plan-cta--primary"
                onClick={startCheckout}
                disabled={isStartingCheckout}
              >
                {isStartingCheckout ? 'Åbner betaling...' : `Opgrader nu — ${PREMIUM_PRICE_SHORT_LABEL}`}
              </button>
              <CheckoutErrorNotice message={checkoutError} onRetry={startCheckout} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Latest Reports */}
      <section className={`landing-section latest-reports-section ${isInView ? 'visible' : ''}`} ref={latestReportsRef}>
        <motion.div variants={latestReportsVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
          <motion.h2 variants={fadeUp}>Seneste rapportopdateringer</motion.h2>
          <motion.p variants={fadeUp}>
            Se de seneste tilsynsrapporter fra daginstitutioner i Danmark. Klik på en institution for at se detaljeret
            analyse.
          </motion.p>

          {reportsLoading ? (
            <motion.div variants={fadeUp} className="latest-reports-loading">
              <LoadingSpinner message="Indlæser seneste rapporter..." size="medium" />
            </motion.div>
          ) : latestReports.length > 0 ? (
            <motion.div variants={latestReportsVariants} className="latest-reports-grid">
              {latestReports.map((item) => {
                const formatDate = (dateString) => {
                  const date = new Date(dateString);
                  const danishMonths = [
                    'januar', 'februar', 'marts', 'april', 'maj', 'juni',
                    'juli', 'august', 'september', 'oktober', 'november', 'december',
                  ];
                  return `${date.getDate()}. ${danishMonths[date.getMonth()]} ${date.getFullYear()}`;
                };

                const capitalizeInstitutionType = (type) => {
                  if (!type) return 'Ikke angivet';
                  return type.charAt(0).toUpperCase() + type.slice(1);
                };

                return (
                  <motion.div
                    key={item.report.id}
                    variants={reportCardVariants}
                    className="latest-report-card"
                    onClick={() => navigate(`/institution/${item.institution.slug}`)}
                    style={{ cursor: 'pointer' }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="latest-report-header">
                      <h3 className="latest-report-institution-name">{item.institution.name}</h3>
                      <div className="latest-report-meta">
                        <span className="latest-report-type">
                          {capitalizeInstitutionType(item.institution.institution_type)}
                        </span>
                        <span className="latest-report-location">📍 {item.institution.city}</span>
                      </div>
                    </div>

                    <div className="latest-report-details">
                      <div className="latest-report-date">
                        <strong>Dato:</strong> {formatDate(item.report.captured_date)}
                      </div>
                      {item.institution.address && (
                        <div className="latest-report-address">
                          <strong>Adresse:</strong> {item.institution.address}
                        </div>
                      )}
                    </div>

                    <div className="latest-report-footer">
                      <span className="view-details-link">Se detaljer →</span>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div variants={reportCardVariants} className="latest-reports-empty">
              <p>Ingen rapporter tilgængelige i øjeblikket.</p>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="landing-cta-section">
        <div className="landing-container landing-container--narrow">
          <motion.div
            variants={containerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="reveal"
          >
            <motion.p variants={fadeUp} className="landing-cta-section__eyebrow">
              <StarIcon />
              Kom godt i gang
            </motion.p>
            <motion.h2 variants={fadeUp} className="landing-section-title">
              Klar til at finde den <em>bedste</em>
              <br />
              vuggestue?
            </motion.h2>
            <motion.p variants={fadeUp} className="landing-section-lead">
              Opgrader til Premium med en engangsbetaling og få adgang til alle funktioner med det samme. Intet
              abonnement, ingen skjulte gebyrer.
            </motion.p>
            <motion.button
              type="button"
              variants={fadeUp}
              className="landing-btn-cta-white"
              onClick={startCheckout}
              disabled={isStartingCheckout}
            >
              <StarIcon size={18} />
              {isStartingCheckout ? 'Åbner betaling...' : PREMIUM_UPGRADE_CTA_LABEL}
            </motion.button>
            {checkoutError && (
              <motion.div variants={fadeUp}>
                <CheckoutErrorNotice message={checkoutError} onRetry={startCheckout} />
              </motion.div>
            )}
            <motion.p variants={fadeUp} className="landing-cta-note">
              {PREMIUM_PAYMENT_TRUST_LINE}
            </motion.p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
